import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { AlertCircle, Info } from "lucide-react";
import { Input, Field, Checkbox } from "@/components/ui";
import { SmartLinkUtmInfo } from "./SmartLinkUtmInfo";
import { SmartLinkPreview } from "./SmartLinkPreview";
import { SmartLinkExtraParam } from "@/services/bedrock";

interface SmartLinkFormViewProps {
  form: UseFormReturn<any>;
  isSubmitting: boolean;
  isFetchingMetadata: boolean;
  onRefreshMetadata: () => void;
  isGlobalEdit: boolean;
  hasExistingUtms: boolean;
  destinationUrl: string;
  smartLinkId?: number;
  isCreatingNewLink?: boolean;
  pendingParams?: Partial<SmartLinkExtraParam>[];
  onPendingParamsChange?: (params: Partial<SmartLinkExtraParam>[]) => void;
}

export const SmartLinkFormView: React.FC<SmartLinkFormViewProps> = ({
  form,
  isSubmitting,
  isFetchingMetadata,
  onRefreshMetadata,
  isGlobalEdit,
  hasExistingUtms,
  destinationUrl,
  smartLinkId,
  isCreatingNewLink,
  pendingParams,
  onPendingParamsChange,
}) => {
  const ogTitle = form.watch("ogTitle");
  const ogDescription = form.watch("ogDescription");
  const ogImageUrl = form.watch("ogImageUrl");

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_350px] gap-6">
      {isGlobalEdit && (
        <div className="col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3 text-amber-900 shadow-sm">
          <AlertCircle className="size-5 shrink-0 text-amber-600" />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold">Global Edit Warning</span>
            <p className="text-sm leading-relaxed opacity-90">
              You are editing a link that is already active. Saving these changes will update the destination for{" "}
              <strong>all published posts</strong> using this link.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-6">
        <Field.Group>
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label>Name</Field.Label>
                <Input {...field} disabled={isSubmitting} placeholder="E.g. My Website" />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <Controller
            name="destinationUrl"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <Field.Label>Destination URL</Field.Label>
                <Input {...field} disabled={isSubmitting} placeholder="https://example.com" type="url" />
                {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </Field.Group>

        <div className={"flex flex-col gap-2"}>
          {hasExistingUtms && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-3 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-blue-100 rounded text-blue-600">
                  <Info className="size-4" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-bold text-slate-900">Existing UTMs Detected</span>
                  <p className="text-sm leading-relaxed text-slate-600">
                    We found tracking parameters already in your URL. SocialCoach adds its own dynamic tracking to
                    provide you with detailed performance reports.
                  </p>
                </div>
              </div>

              <Controller
                name="stripExistingUtms"
                control={form.control}
                render={({ field }) => (
                  <div className="flex space-x-2 items-end">
                    <Checkbox
                      id="strip-utms"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="bg-white mt-2"
                    />
                    <label
                      htmlFor="strip-utms"
                      className="text-sm font-medium text-slate-700 cursor-pointer select-none"
                    >
                      Replace existing UTMs with SocialCoach dynamic tracking (Recommended)
                    </label>
                  </div>
                )}
              />
            </div>
          )}

          <SmartLinkUtmInfo
            smartLinkId={smartLinkId}
            isCreatingNewLink={isCreatingNewLink}
            pendingParams={pendingParams}
            onPendingParamsChange={onPendingParamsChange}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <SmartLinkPreview
          ogTitle={ogTitle}
          ogDescription={ogDescription}
          ogImageUrl={ogImageUrl}
          isFetchingMetadata={isFetchingMetadata}
          onRefresh={onRefreshMetadata}
          destinationUrl={destinationUrl}
        />
      </div>
    </div>
  );
};
